import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Mail, Lock, User, Eye, EyeOff } from "lucide-react";

function formatApiError(detail) {
  if (detail == null) return "Error al procesar la solicitud";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const LoginPage = () => {
  const { setUser, login: googleLogin } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResult, setForgotResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const payload = isRegister
        ? { email: form.email, password: form.password, name: form.name }
        : { email: form.email, password: form.password };

      const { data } = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });

      if (data.success && data.user) {
        setUser(data.user);
        const subStatus = data.user.suscripcion_status;
        const role = data.user.role;
        if (subStatus === "expired") {
          toast.info("Tu suscripcion ha expirado. Renueva para acceder a todas las funciones.");
        } else if (role !== "superadmin" && (!subStatus || subStatus === "pending" || subStatus === "under_review")) {
          toast.success("Bienvenido. Tu plan esta pendiente de aprobacion.");
        } else {
          toast.success(isRegister ? "Cuenta creada" : "Bienvenido de vuelta");
        }
        // Redirect based on status
        if (role !== "superadmin" && (!subStatus || subStatus === "pending" || subStatus === "under_review")) {
          navigate("/panel/mi-suscripcion", { replace: true });
        } else {
          navigate("/panel", { replace: true });
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 && detail.includes("activada")) {
        toast.error(detail);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotResult(data);
      toast.success("Enlace de recuperacion generado");
    } catch (err) {
      toast.error("Error al procesar solicitud");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="font-display text-3xl tracking-tight text-rose-900">
              Celebra<span className="font-bold">Mid</span>
            </h1>
          </Link>
          <p className="text-stone-500 mt-2 text-sm">
            {isRegister ? "Crea tu cuenta" : "Inicia sesión en tu panel"}
          </p>
        </div>

        <Card className="border-stone-200 shadow-sm">
          <CardContent className="p-6 space-y-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="name"
                      placeholder="Tu nombre"
                      value={form.name}
                      onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                      className="pl-10 border-stone-300"
                      data-testid="input-register-name"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                    className="pl-10 border-stone-300"
                    required
                    data-testid="input-login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min. 6 caracteres"
                    value={form.password}
                    onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                    className="pl-10 pr-10 border-stone-300"
                    required
                    minLength={6}
                    data-testid="input-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="btn-login-submit">
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  isRegister ? "Crear cuenta" : "Iniciar sesion"
                )}
              </Button>

              {!isRegister && (
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  className="text-xs text-rose-700 hover:underline w-full text-right"
                  data-testid="btn-forgot-password"
                >
                  Olvide mi contrasena
                </button>
              )}
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-stone-400">o continúa con</span>
              </div>
            </div>

            {/* Google OAuth */}
            <Button
              type="button"
              variant="outline"
              className="w-full border-stone-300 text-stone-700 hover:bg-stone-50"
              onClick={googleLogin}
              data-testid="btn-google-login"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </Button>

            {/* Toggle */}
            <p className="text-center text-sm text-stone-500">
              {isRegister ? "¿Ya tienes cuenta?" : "¿No tienes cuenta?"}{" "}
              <button
                type="button"
                onClick={() => setIsRegister(!isRegister)}
                className="text-rose-800 font-medium hover:underline"
                data-testid="btn-toggle-auth-mode"
              >
                {isRegister ? "Inicia sesión" : "Regístrate"}
              </button>
            </p>
          </CardContent>
        </Card>

        {/* Forgot Password Dialog */}
        {showForgot && (
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <h3 className="font-display text-lg text-stone-900">Recuperar contrasena</h3>
              {!forgotResult ? (
                <>
                  <p className="text-sm text-stone-500">Ingresa tu email y te generaremos una contrasena temporal.</p>
                  <Input type="email" placeholder="tu@email.com" value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="border-stone-300" data-testid="input-forgot-email" />
                  <div className="flex gap-2">
                    <Button onClick={handleForgotPassword} disabled={forgotLoading} className="btn-primary flex-1" data-testid="btn-forgot-submit">
                      {forgotLoading ? "Procesando..." : "Generar contrasena"}
                    </Button>
                    <Button variant="outline" onClick={() => setShowForgot(false)} className="border-stone-300">Cancelar</Button>
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  {forgotResult.reset_token ? (
                    <div className="bg-rose-50 border border-rose-200 rounded-sm p-3 space-y-2">
                      <p className="text-sm font-medium text-rose-900">Enlace de recuperacion:</p>
                      <div className="bg-white border border-rose-200 rounded p-2">
                        <code className="text-xs break-all select-all">{window.location.origin}/reset-password/{forgotResult.reset_token}</code>
                      </div>
                      <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/reset-password/${forgotResult.reset_token}`); toast.success("Enlace copiado"); }}
                        className="text-xs text-rose-700 underline">
                        Copiar enlace
                      </button>
                      <p className="text-xs text-rose-700">Abre este enlace para definir tu nueva contrasena. Valido por 24 horas.</p>
                    </div>
                  ) : (
                    <div className="bg-rose-50 border border-rose-200 rounded-sm p-3">
                      <p className="text-sm text-rose-800">{forgotResult.message}</p>
                    </div>
                  )}
                  <Button variant="outline" onClick={() => { setShowForgot(false); setForgotResult(null); setForgotEmail(""); }}
                    className="border-stone-300 w-full">Cerrar</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
